import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Crown,
  Eye,
  FileCheck,
  Mail,
  Phone,
  Plus,
  Settings,
  Star,
  User,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { billingAPI, employeeAPI } from "../../services/api";
import employeeService from "../../services/employee";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Progress } from "../ui/progress";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useToast } from "../ui/use-toast";

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface Employee {
  employeeId: string;
  fullName: string;
  email?: string;
  phoneE164?: string;
  status: string;
  createdAt: string;
  isUKNative?: boolean;
  ukNativeType?: string;
  tags?: Tag[];
  verificationSteps?: {
    email?: { status: string; completedAt?: string };
    phone?: { status: string; completedAt?: string };
    id?: { status: string; completedAt?: string };
    rtw?: { status: string; completedAt?: string };
  };
}

interface EmployeeDetail extends Employee {
  inviteChannelPref?: string;
  expiresAt?: string;
  lastActivityAt?: string;
  verificationToken?: string;
  isUKNative?: boolean;
  ukNativeType?: string;
  tags?: Tag[];
}

const Dashboard: React.FC = () => {
  const { user, org } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState<any>(null);
  const [currentPlan, setCurrentPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<{
    totalEmployees: number;
    verifiedEmployees: number;
    pendingVerifications: number;
  } | null>(null);
  const [statisticsLoading, setStatisticsLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeDetail | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadDashboardData();
    loadStatistics();
    loadEmployees();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [usageResponse, currentPlanResponse] = await Promise.all([
        billingAPI.getUsage(),
        billingAPI.getCurrentPlan(),
      ]);

      setUsage(usageResponse.data);
      setCurrentPlan(currentPlanResponse.data);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      setStatisticsLoading(true);
      const response = await employeeAPI.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error("Failed to load statistics:", error);
    } finally {
      setStatisticsLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await employeeAPI.listEmployees({ limit: "10" });
      setEmployees(response.data.items || []);
    } catch (error) {
      console.error("Failed to load employees:", error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStepStatus = (status?: string) => {
    if (!status) {
      return (
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-gray-400" />
          <span className="text-xs text-gray-400">Pending</span>
        </div>
      );
    }

    const statusConfig = {
      pass: { icon: CheckCircle, color: "text-green-600", label: "Passed" },
      fail: { icon: XCircle, color: "text-red-600", label: "Failed" },
      pending: { icon: Clock, color: "text-yellow-600", label: "Pending" },
      skipped: { icon: CheckCircle, color: "text-green-600", label: "Skipped" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] ||
      statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <div className="flex items-center gap-1">
        <IconComponent className={`h-3 w-3 ${config.color}`} />
        <span className={`text-xs ${config.color}`}>{config.label}</span>
      </div>
    );
  };

  const handleEmployeeClick = async (employee: Employee) => {
    try {
      const detail = await employeeService.getEmployeeDetail(
        employee.employeeId
      );

      // Normalize backend detail to component shape
      const stepsArray = detail.flowStatus?.steps || [];
      const verificationsArray = detail.verifications || [];
      const stepsObj: any = {
        email: { status: "pending" },
        phone: { status: "pending" },
        id: { status: "pending" },
        rtw: { status: "pending" },
      };
      stepsArray.forEach((s: any) => {
        if (stepsObj[s.type] !== undefined) {
          // Find corresponding verification to get createdAt
          const verification = verificationsArray.find(
            (v: any) => v.type === s.type
          );
          stepsObj[s.type] = {
            status: s.status,
            completedAt: verification?.createdAt,
          };
        }
      });

      const normalized: EmployeeDetail = {
        employeeId: detail.employee?.employeeId || employee.employeeId,
        orgId: detail.employee?.orgId || (employee as any).orgId,
        fullName: detail.employee?.fullName || employee.fullName,
        email: detail.employee?.email || employee.email,
        phoneE164: detail.employee?.phoneE164 || employee.phoneE164,
        status: detail.employee?.status || employee.status,
        candidateId: detail.employee?.candidateId,
        badgeUrl: detail.employee?.badgeUrl,
        source: detail.employee?.source || (employee as any).source,
        createdAt: detail.employee?.createdAt || employee.createdAt,
        inviteChannelPref: detail.employee?.inviteChannelPref,
        expiresAt: detail.employee?.expiresAt,
        lastActivityAt: detail.employee?.lastActivityAt,
        verificationToken: detail.employee?.verificationToken,
        verificationSteps: stepsObj,
        isUKNative: detail.employee?.isUKNative || employee.isUKNative,
        ukNativeType: detail.employee?.ukNativeType || employee.ukNativeType,
        tags: detail.employee?.tags || employee.tags || [],
      } as any;

      setSelectedEmployee(normalized);
      setIsDialogOpen(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const allStepsPassed = (e: Employee) => {
    const steps = e.verificationSteps;
    if (!steps) return false;
    const emailPassed = steps.email?.status === "pass";
    const phonePassed = steps.phone?.status === "pass";
    const idPassed = steps.id?.status === "pass";
    const rtwPassed =
      steps.rtw?.status === "pass" || steps.rtw?.status === "skipped";
    return emailPassed && phonePassed && idPassed && rtwPassed;
  };

  const getStatusBadge = (employee: Employee) => {
    // If all steps are passed/skipped, show as verified
    const isVerified = allStepsPassed(employee);
    const displayStatus = isVerified ? "verified" : employee.status;

    const statusConfig = {
      invited: {
        variant: "secondary" as const,
        icon: Clock,
        label: "Invited",
        className: "",
      },
      verifying: {
        variant: "default" as const,
        icon: AlertCircle,
        label: "Verifying",
        className: "",
      },
      verified: {
        variant: "default" as const,
        icon: CheckCircle,
        label: "Verified",
        className: "bg-green-500 hover:bg-green-600 text-white border-green-600",
      },
      completed: {
        variant: "default" as const,
        icon: CheckCircle,
        label: "Completed",
        className: "bg-green-500 hover:bg-green-600 text-white border-green-600",
      },
      failed: {
        variant: "destructive" as const,
        icon: XCircle,
        label: "Failed",
        className: "",
      },
    };

    const config =
      statusConfig[displayStatus as keyof typeof statusConfig] ||
      statusConfig.invited;
    const IconComponent = config.icon;

    return (
      <Badge
        variant={config.variant}
        className={`flex items-center gap-1 ${config.className}`}
      >
        <IconComponent className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPlanIcon = (planName: string) => {
    switch (planName?.toLowerCase()) {
      case "basic":
        return <Zap className="h-5 w-5 text-blue-500" />;
      case "premium":
        return <Star className="h-5 w-5 text-purple-500" />;
      case "enterprise":
        return <Crown className="h-5 w-5 text-emerald-500" />;
      default:
        return <CreditCard className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPlanColor = (planName: string) => {
    switch (planName?.toLowerCase()) {
      case "basic":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300";
      case "premium":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300";
      case "enterprise":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300";
    }
  };

  const stats = [
    {
      title: "Total Employees",
      value: statisticsLoading
        ? "..."
        : (statistics?.totalEmployees || 0).toString(),
      description: "Employees in your organization",
      icon: Users,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
      loading: statisticsLoading,
    },
    {
      title: "Verified Employees",
      value: statisticsLoading
        ? "..."
        : (statistics?.verifiedEmployees || 0).toString(),
      description: "Successfully verified employees",
      icon: FileCheck,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
      loading: statisticsLoading,
    },
    {
      title: "Pending Verifications",
      value: statisticsLoading
        ? "..."
        : (statistics?.pendingVerifications || 0).toString(),
      description: "Awaiting verification",
      icon: Settings,
      color: "text-yellow-600 dark:text-yellow-400",
      bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
      loading: statisticsLoading,
    },
    {
      title: "Monthly Usage",
      value: usage ? `${usage.checksUsed}/${usage.checksIncluded}` : "0/0",
      description: "Identity verifications this month",
      icon: BarChart3,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
      loading: loading,
    },
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Welcome back, {user?.name || "User"}!
            </h1>
            <p className="text-muted-foreground text-lg mt-2">
              Here's what's happening with your organization:{" "}
              {org?.name || "Your Organization"}
            </p>
          </div>
          <Button
            onClick={() => navigate("/widget")}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Employee
          </Button>
        </div>

        {/* Current Plan Status */}
        {loading ? (
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-6 w-32" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Plan Info Skeleton */}
                <div className="space-y-2">
                  <Skeleton className="h-7 w-24" />
                  <Skeleton className="h-6 w-16" />
                </div>

                {/* Usage Info Skeleton */}
                <div className="space-y-2">
                  <Skeleton className="h-5 w-28" />
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-2 w-full mt-2" />
                  </div>
                </div>

                {/* Days Remaining Skeleton */}
                <div className="space-y-2">
                  <Skeleton className="h-5 w-28" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-4 w-36" />
                </div>
              </div>
            </CardContent>
          </Card>
        ) : currentPlan ? (
          <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getPlanIcon(currentPlan.planName)}
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Plan Info */}
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold capitalize">
                    {currentPlan.planName === "free"
                      ? "Free"
                      : currentPlan.planName}
                  </h3>
                  <Badge className={getPlanColor(currentPlan.planName)}>
                    {currentPlan.planStatus === "active"
                      ? "Active"
                      : "Inactive"}
                  </Badge>
                </div>

                {/* Usage Info */}
                {usage && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-foreground">
                      Monthly Usage
                    </h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>
                        Used: {usage.checksUsed} of {usage.checksIncluded}
                      </div>
                      <div>Remaining: {usage.remainingChecks}</div>
                      {usage.checksIncluded > 0 && (
                        <Progress
                          value={
                            (usage.checksUsed / usage.checksIncluded) * 100
                          }
                          className="h-2 mt-2"
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Days Remaining */}
                {usage && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-foreground">
                      Time Remaining
                    </h4>
                    <div className="text-2xl font-bold text-primary">
                      {usage.daysRemaining} days
                    </div>
                    <p className="text-sm text-muted-foreground">
                      until current period ends
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Statistics Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card
                key={index}
                className="hover:shadow-lg transition-shadow duration-200"
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-full ${stat.bgColor}`}>
                    <IconComponent className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  {stat.loading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-8 w-16" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ) : (
                    <>
                  <div className="text-2xl font-bold text-foreground">
                    {stat.value}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {stat.description}
                  </p>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Employees Table */}
        <Card className="relative">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Employees</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/widget")}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Employee
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/employees")}
                >
                  See All
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
            {employeesLoading ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              </div>
            ) : employees.length === 0 ? (
              <div className="py-12 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full bg-muted">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2">No employees yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get started by adding your first employee.
                </p>
                <Button onClick={() => navigate("/widget")}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Employee
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Tags</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Verification Steps</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow
                        key={employee.employeeId}
                        className="hover:bg-muted/50"
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium">
                                {employee.fullName || "N/A"}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                ID: {employee.employeeId.slice(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {employee.email && (
                              <div className="flex items-center gap-1 text-sm">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                {employee.email}
                              </div>
                            )}
                            {employee.phoneE164 && (
                              <div className="flex items-center gap-1 text-sm">
                                <Phone className="h-3 w-3 text-muted-foreground" />
                                {employee.phoneE164}
                              </div>
                            )}
                            {!employee.email && !employee.phoneE164 && (
                              <span className="text-sm text-muted-foreground">
                                No contact info
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {employee.tags && employee.tags.length > 0 ? (
                              employee.tags.map((tag) => (
                                <Badge
                                  key={tag.id}
                                  variant="outline"
                                  className="text-xs"
                                  style={{
                                    backgroundColor: `${tag.color}20`,
                                    borderColor: tag.color,
                                    color: tag.color,
                                  }}
                                >
                                  {tag.name}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(employee)}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="text-xs">
                              Email:{" "}
                              {getStepStatus(
                                employee.verificationSteps?.email?.status
                              )}
                            </div>
                            <div className="text-xs">
                              Phone:{" "}
                              {getStepStatus(
                                employee.verificationSteps?.phone?.status
                              )}
                            </div>
                            <div className="text-xs">
                              ID:{" "}
                              {getStepStatus(
                                employee.verificationSteps?.id?.status
                              )}
                            </div>
                            <div className="text-xs">
                              RTW:{" "}
                              {getStepStatus(
                                employee.verificationSteps?.rtw?.status
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {formatDate(employee.createdAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                  <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEmployeeClick(employee)}
                  >
                            <Eye className="h-4 w-4" />
                  </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
                </CardContent>
              </Card>

        {/* Employee Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Employee Details</DialogTitle>
              <DialogDescription>
                Complete information about this employee's verification process
              </DialogDescription>
            </DialogHeader>

            {selectedEmployee && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Full Name
                    </label>
                    <p className="text-lg font-semibold">
                      {selectedEmployee.fullName}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Status
                    </label>
                    <div className="mt-1">
                      {getStatusBadge(selectedEmployee)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Email
                    </label>
                    <p className="text-sm">
                      {selectedEmployee.email || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Phone
                    </label>
                    <p className="text-sm">
                      {selectedEmployee.phoneE164 || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Invite Channel
                    </label>
                    <p className="text-sm capitalize">
                      {selectedEmployee.inviteChannelPref || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Employee ID
                    </label>
                    <p className="text-sm font-mono">
                      {selectedEmployee.employeeId}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedEmployee.tags && selectedEmployee.tags.length > 0 ? (
                        selectedEmployee.tags.map((tag) => (
                          <Badge
                            key={tag.id}
                            variant="outline"
                            style={{
                              backgroundColor: `${tag.color}20`,
                              borderColor: tag.color,
                              color: tag.color,
                            }}
                          >
                            {tag.name}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">No tags</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* UK Native Status */}
                {selectedEmployee.ukNativeType && (
                  <div className="mb-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <div>
                        <p className="text-sm font-medium text-green-900 dark:text-green-100">
                          UK Status: {
                            selectedEmployee.ukNativeType === "uk_ireland" ? "UK/Ireland national" :
                            selectedEmployee.ukNativeType === "eea_settlement" ? "EEA national with eVisas or EU Settlement Scheme" :
                            "Others with physical BRP/visa vignette"
                          }
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300">
                          This employee has UK status. UK Work Permit
                          verification was skipped. Verification completed with 3
                          steps (Email, Phone, ID).
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Verification Steps */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">
                    Verification Steps
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium">Email Verification</div>
                          <div className="text-sm text-muted-foreground">
                            {selectedEmployee.verificationSteps?.email?.status ===
                            "pass"
                              ? selectedEmployee.verificationSteps.email
                                  .completedAt
                                ? `Completed: ${formatDate(
                                    selectedEmployee.verificationSteps.email
                                      .completedAt
                                  )}`
                                : "Completed"
                              : "Not completed"}
                          </div>
                        </div>
                        {getStepStatus(
                          selectedEmployee.verificationSteps?.email?.status
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium">Phone Verification</div>
                          <div className="text-sm text-muted-foreground">
                            {selectedEmployee.verificationSteps?.phone?.status ===
                            "pass"
                              ? selectedEmployee.verificationSteps.phone
                                  .completedAt
                                ? `Completed: ${formatDate(
                                    selectedEmployee.verificationSteps.phone
                                      .completedAt
                                  )}`
                                : "Completed"
                              : "Not completed"}
                          </div>
                        </div>
                        {getStepStatus(
                          selectedEmployee.verificationSteps?.phone?.status
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium">ID Verification</div>
                          <div className="text-sm text-muted-foreground">
                            {selectedEmployee.verificationSteps?.id?.status ===
                            "pass"
                              ? selectedEmployee.verificationSteps.id
                                  .completedAt
                                ? `Completed: ${formatDate(
                                    selectedEmployee.verificationSteps.id
                                      .completedAt
                                  )}`
                                : "Completed"
                              : "Not completed"}
                          </div>
                        </div>
                        {getStepStatus(
                          selectedEmployee.verificationSteps?.id?.status
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium">Right to Work</div>
                          <div className="text-sm text-muted-foreground">
                            {selectedEmployee.verificationSteps?.rtw?.status ===
                            "skipped"
                              ? "Skipped (UK Native)"
                              : selectedEmployee.verificationSteps?.rtw?.status ===
                                "pass"
                              ? selectedEmployee.verificationSteps.rtw
                                  .completedAt
                                ? `Completed: ${formatDate(
                                    selectedEmployee.verificationSteps.rtw
                                      .completedAt
                                  )}`
                                : "Completed"
                              : "Not completed"}
                          </div>
                        </div>
                        {getStepStatus(
                          selectedEmployee.verificationSteps?.rtw?.status
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Created At
                    </label>
                    <p className="text-sm">
                      {formatDate(selectedEmployee.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Expires At
                    </label>
                    <p className="text-sm">
                      {selectedEmployee.expiresAt
                        ? formatDate(selectedEmployee.expiresAt)
                        : "Not set"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Verification Token
                    </label>
                    <p className="text-sm font-mono break-all">
                      {selectedEmployee.verificationToken || "Not available"}
                    </p>
                  </div>
                  {selectedEmployee.lastActivityAt && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Last Activity
                      </label>
                      <p className="text-sm">
                        {formatDate(selectedEmployee.lastActivityAt)}
                      </p>
                    </div>
                  )}
                </div>
        </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Dashboard;
